import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, CircularProgress, Menu, MenuItem } from '@mui/material';

import {
  PaginationDivider,
  PaginationFooter,
  PaginationIconButton,
  PaginationLabel,
  PaginationLeftSection,
  PaginationPill,
  PaginationRightSection,
  PaginationValue,
} from './GeneratikTable.styles';
import { useGeneratikTableContext } from './GeneratikTableContext';
import {
  actionsRowSx,
  pageSizeArrowIconSx,
  pageSizeMenuPaperSx,
  paginationInfoPillSx,
  paginationRowSx,
  rightSectionSx,
  stackedFooterSx,
} from './GeneratikTableDialogDisplayFooter.styles';

const PAGINATION_TEST_ID = 'table-pagination-row';
const ACTIONS_TEST_ID = 'table-modal-actions-row';

interface Props {
  pageSize: number;
  rowCount: number;
  startRow: number;
  endRow: number;
  pageSizeOptions: number[];
  pageTotal?: number | string;
  pageSelected?: number | string;
  hidePagination?: boolean;
  selectedRowsCount?: number;
  pageSizeAnchor: HTMLElement | null;
  handlePageSizeSelect: (size: number) => void;
  formatTotal: (value: number | string | undefined) => string;
  handlePageSizeClick: (event: React.MouseEvent<HTMLElement>) => void;
  handlePageSizeClose: () => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  modalActions: ReactNode;
  stackedModalFooter?: boolean;
}
const GeneratikTableDialogDisplayFooter = ({
  pageSize,
  rowCount,
  startRow,
  endRow,
  pageSizeOptions,
  pageTotal,
  pageSelected,
  hidePagination,
  selectedRowsCount,
  pageSizeAnchor,
  handlePageSizeSelect,
  formatTotal,
  handlePageSizeClick,
  handlePageSizeClose,
  handlePreviousPage,
  handleNextPage,
  canGoPrevious,
  canGoNext,
  modalActions,
  stackedModalFooter,
}: Props) => {
  const { t } = useTranslation();
  const ctx = useGeneratikTableContext();

  const paginationControls = (
    <>
      {!hidePagination && (
        <>
          <PaginationPill onClick={handlePageSizeClick} data-testid="page-size-selector">
            <PaginationValue>{pageSize}</PaginationValue>
            <PaginationLabel>{t('per page')}</PaginationLabel>
            <KeyboardArrowDownIcon sx={pageSizeArrowIconSx} />
          </PaginationPill>
          <Menu
            anchorEl={pageSizeAnchor}
            open={Boolean(pageSizeAnchor)}
            onClose={handlePageSizeClose}
            slotProps={{ paper: { sx: pageSizeMenuPaperSx } }}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem
                key={size}
                onClick={() => handlePageSizeSelect(size)}
                selected={size === pageSize}
                dense
              >
                {size}
              </MenuItem>
            ))}
          </Menu>

          <PaginationPill sx={paginationInfoPillSx} data-testid="pagination-info">
            <PaginationValue>
              {startRow}-{endRow}
            </PaginationValue>
            <PaginationLabel>{t('of')}</PaginationLabel>
            <PaginationLabel>{rowCount}</PaginationLabel>

            <PaginationDivider />

            <PaginationIconButton
              onClick={handlePreviousPage}
              disabled={!canGoPrevious}
              aria-label="Go to previous page"
              data-testid="pagination-prev"
            >
              <KeyboardArrowLeftIcon />
            </PaginationIconButton>
            <PaginationIconButton
              onClick={handleNextPage}
              disabled={!canGoNext}
              aria-label="Go to next page"
              data-testid="pagination-next"
            >
              <KeyboardArrowRightIcon />
            </PaginationIconButton>
          </PaginationPill>
        </>
      )}

      {!!selectedRowsCount && (
        <PaginationPill>
          <PaginationLabel>{t('Selected Rows')} :</PaginationLabel>
          <PaginationValue>{selectedRowsCount.toLocaleString()}</PaginationValue>
        </PaginationPill>
      )}

      {!!pageSelected && (
        <PaginationPill data-testid="page-selected">
          <PaginationLabel>{t('Page Selected')} :</PaginationLabel>
          <PaginationValue>{formatTotal(pageSelected)}</PaginationValue>
        </PaginationPill>
      )}

      {pageTotal !== undefined && (
        <PaginationPill data-testid="page-total">
          <PaginationLabel>{t('Page Total')} :</PaginationLabel>
          <PaginationValue>{formatTotal(pageTotal)}</PaginationValue>
        </PaginationPill>
      )}
    </>
  );

  const actions = (
    <>
      {ctx.loading && <CircularProgress data-testid="table-loading-progress" size={25} />}
      {modalActions}
    </>
  );

  if (!stackedModalFooter) {
    return (
      <PaginationFooter isModal>
        <PaginationLeftSection data-testid={PAGINATION_TEST_ID}>
          {paginationControls}
        </PaginationLeftSection>

        <PaginationRightSection sx={rightSectionSx} data-testid={ACTIONS_TEST_ID}>
          {actions}
        </PaginationRightSection>
      </PaginationFooter>
    );
  }

  // Stacked layout: an empty pagination row would only contribute the column gap, so it goes away.
  const hasPaginationRow =
    !hidePagination || !!selectedRowsCount || !!pageSelected || pageTotal !== undefined;

  return (
    <PaginationFooter isModal sx={stackedFooterSx}>
      {hasPaginationRow && (
        <Box sx={paginationRowSx} data-testid={PAGINATION_TEST_ID}>
          {paginationControls}
        </Box>
      )}

      <Box sx={actionsRowSx} data-testid={ACTIONS_TEST_ID}>
        {actions}
      </Box>
    </PaginationFooter>
  );
};

export default GeneratikTableDialogDisplayFooter;
